import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import axios from 'axios';
import { getSessionId } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [bikeChoiceOpen, setBikeChoiceOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [homeContent, setHomeContent] = useState(null);

  useEffect(() => {
    axios.post(`${API}/visitor/log`, {
      page: 'home',
      session_id: getSessionId(),
      user_agent: navigator.userAgent,
      referrer: document.referrer
    }).catch(() => {});

    // Fetch home page content from CMS
    const fetchHomeContent = async () => {
      try {
        const response = await axios.get(`${API}/home-content`);
        setHomeContent(response.data);
      } catch (error) {
        // Error handled silently
      }
    };
    fetchHomeContent();

    // Fetch products from API
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        const apiProducts = response.data;
        
        // Map API products to display format
        const folgo = apiProducts.find(p => p.id === 'folgo');
        const cargo = apiProducts.find(p => p.id === 'cargo');
        
        const displayProducts = [
          folgo && {
            id: folgo.id,
            name: folgo.name,
            description: language === 'en' && folgo.description_en ? folgo.description_en : folgo.description,
            price: `${folgo.base_price.toLocaleString('fr-FR')} €`,
            image: folgo.images?.[0] || '',
            href: '/folgo'
          },
          cargo && {
            id: cargo.id,
            name: cargo.name,
            description: language === 'en' && cargo.description_en ? cargo.description_en : cargo.description,
            price: `${cargo.base_price.toLocaleString('fr-FR')} €`,
            image: cargo.images?.[0] || '',
            href: '/cargo'
          },
          {
            id: 'kit',
            name: 'KIT CADRE',
            description: language === 'fr' ? 'Kit cadre pour montage personnalisé' : 'Frame kit for custom build',
            price: `${t('products.fromPrice')} 1 100 €`,
            image: 'https://customer-assets.emergentagent.com/job_belleville-bikes/artifacts/yxdx7hsn_KIt%20cadre%20ok.jpg',
            href: '/kit-cadre'
          }
        ].filter(Boolean);
        
        setProducts(displayProducts);
      } catch (error) {
        // Error handled silently
        // Fallback
        setProducts([
          {
            id: 'folgo',
            name: 'FOLGO',
            description: language === 'fr' ? 'Vélo cargo PLIANT compact et polyvalent' : 'Compact and versatile FOLDING cargo bike',
            price: '3 490 €',
            image: 'https://customer-assets.emergentagent.com/job_belleville-bikes/artifacts/x78evn8g_FOLGO%20OK%20DER%20%2B.jpg',
            href: '/folgo'
          },
          {
            id: 'cargo',
            name: 'CARGO CLASSIQUE',
            description: language === 'fr' ? 'Vélo cargo grande capacité' : 'High capacity cargo bike',
            price: '2 650 €',
            image: 'https://customer-assets.emergentagent.com/job_belleville-bikes/artifacts/yiav7i8c_cargo%20classique%20vert%20FB.jpg',
            href: '/cargo'
          },
          {
            id: 'kit',
            name: 'KIT CADRE',
            description: language === 'fr' ? 'Kit cadre pour montage personnalisé' : 'Frame kit for custom build',
            price: `${t('products.fromPrice')} 1 190 €`,
            image: 'https://customer-assets.emergentagent.com/job_belleville-bikes/artifacts/yxdx7hsn_KIt%20cadre%20ok.jpg',
            href: '/kit-cadre'
          }
        ]);
      }
    };

    fetchProducts();
  }, [language, t]);

  // Helper to get CMS text with fallback
  const getCmsText = (key) => {
    if (!homeContent) return t(key);
    const langKey = language === 'en' ? `${key.split('.').pop()}_en` : `${key.split('.').pop()}_fr`;
    // Map t() keys to CMS keys
    const keyMap = {
      'hero.subtitle': language === 'en' ? 'hero_subtitle_en' : 'hero_subtitle_fr',
      'hero.title1': language === 'en' ? 'hero_title1_en' : 'hero_title1_fr',
      'hero.title2': language === 'en' ? 'hero_title2_en' : 'hero_title2_fr',
      'hero.description': language === 'en' ? 'hero_description_en' : 'hero_description_fr',
      'cta.button': language === 'en' ? 'hero_cta_en' : 'hero_cta_fr',
      'hero.ctaContact': language === 'en' ? 'hero_cta_contact_en' : 'hero_cta_contact_fr',
      'products.subtitle': language === 'en' ? 'products_subtitle_en' : 'products_subtitle_fr',
      'products.title': language === 'en' ? 'products_title_en' : 'products_title_fr',
    };
    const cmsKey = keyMap[key];
    return homeContent[cmsKey] || t(key);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="order-2 lg:order-1"
            >
              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                {getCmsText('hero.subtitle')}
              </span>
              <h1 className="font-heading font-black text-5xl md:text-7xl tracking-tight leading-none mt-4">
                {getCmsText('hero.title1')}
                <br />
                <span className="text-[#0047AB]">{getCmsText('hero.title2')}</span>
              </h1>
              <p className="font-body text-lg text-muted-foreground mt-6 max-w-md leading-relaxed">
                {getCmsText('hero.description')}
              </p>
              <div className="flex flex-wrap gap-4 mt-8">
                <Button 
                  onClick={() => setBikeChoiceOpen(true)}
                  className="h-12 px-8 rounded-none font-mono uppercase tracking-widest" 
                  data-testid="cta-discover"
                >
                  {getCmsText('cta.button')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Link to="/contact">
                  <Button variant="outline" className="h-12 px-8 rounded-none font-mono uppercase tracking-widest" data-testid="cta-contact">
                    {getCmsText('hero.ctaContact')}
                  </Button>
                </Link>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="order-1 lg:order-2"
            >
              <div className="aspect-square bg-secondary overflow-hidden">
                <img 
                  src={homeContent?.hero_image_url || "https://customer-assets.emergentagent.com/job_belleville-bikes/artifacts/x78evn8g_FOLGO%20OK%20DER%20%2B.jpg"}
                  alt="FOLGO Vélo Cargo"
                  className="w-full h-full object-contain p-8"
                  data-testid="hero-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 md:py-24 bg-secondary" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              {getCmsText('products.subtitle')}
            </span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl uppercase tracking-tight mt-2">
              {getCmsText('products.title')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={product.href} className="group block" data-testid={`product-card-${product.id}`}>
                  <div className="aspect-square bg-white overflow-hidden border border-border group-hover:border-black transition-colors">
                    <img 
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="mt-4">
                    <h3 className="font-heading font-bold text-xl uppercase">{product.name}</h3>
                    <p className="font-body text-sm text-muted-foreground mt-1">{product.description}</p>
                    <p className="font-mono font-bold mt-2">{product.price}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-black text-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tight">
              {t('cta.title')}
            </h2>
            <p className="font-body text-lg text-gray-400 mt-4 max-w-md mx-auto">
              {t('cta.description')}
            </p>
            <Button 
              onClick={() => setBikeChoiceOpen(true)}
              className="mt-8 h-12 px-8 rounded-none font-mono uppercase tracking-widest bg-white text-black hover:bg-[#0047AB] hover:text-white"
              data-testid="cta-configure"
            >
              {t('cta.button')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Made in France */}
      <section className="py-8 bg-white border-t border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="font-heading font-bold text-lg md:text-xl uppercase tracking-wide text-black">
            {language === 'fr' 
              ? 'Vélos cargos conçus et fabriqués en France' 
              : 'Cargo bikes designed and made in France'}
          </p>
        </div>
      </section>

      {/* Bike Choice Dialog */}
      <Dialog open={bikeChoiceOpen} onOpenChange={setBikeChoiceOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-2xl uppercase text-center">
              {language === 'fr' ? 'Choisissez votre vélo' : 'Choose your bike'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            {/* FOLGO */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setBikeChoiceOpen(false);
                navigate('/folgo');
                window.scrollTo(0, 0);
              }}
              className="border-2 border-border hover:border-black transition-colors p-4 text-left"
              data-testid="choose-folgo"
            >
              <div className="aspect-square bg-secondary overflow-hidden mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_belleville-bikes/artifacts/x78evn8g_FOLGO%20OK%20DER%20%2B.jpg"
                  alt="FOLGO"
                  className="w-full h-full object-contain p-4"
                />
              </div>
              <h3 className="font-heading font-black text-xl uppercase">FOLGO</h3>
              <p className="font-body text-sm text-muted-foreground mt-1">
                {language === 'fr' 
                  ? 'Vélo cargo PLIANT compact et polyvalent' 
                  : 'Compact and versatile FOLDING cargo bike'}
              </p>
              <p className="font-mono font-bold text-[#0047AB] mt-2">3 490 €</p>
            </motion.button>

            {/* CARGO CLASSIQUE */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setBikeChoiceOpen(false);
                navigate('/cargo');
                window.scrollTo(0, 0);
              }}
              className="border-2 border-border hover:border-black transition-colors p-4 text-left"
              data-testid="choose-cargo"
            >
              <div className="aspect-square bg-secondary overflow-hidden mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_belleville-bikes/artifacts/yiav7i8c_cargo%20classique%20vert%20FB.jpg"
                  alt="CARGO CLASSIQUE"
                  className="w-full h-full object-contain p-4"
                />
              </div>
              <h3 className="font-heading font-black text-xl uppercase">CARGO CLASSIQUE</h3>
              <p className="font-body text-sm text-muted-foreground mt-1">
                {language === 'fr' 
                  ? 'Vélo cargo grande capacité pour la famille' 
                  : 'High capacity cargo bike for family'}
              </p>
              <p className="font-mono font-bold text-[#0047AB] mt-2">2 650 €</p>
            </motion.button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
